import options from './options'
import {formatNumberAsUSD} from '@/services/utils'

/**
 * "Decisioning" Templates
 *
 * These are templates for messages that admins and staff
 * will send to employees based on decisions they have made
 * about the employees' requests.
 */

// These are the names of the templates
const REQUEST_APPROVED = 'REQUEST_APPROVED'
const REQUEST_PARTIALLY_APPROVED = 'REQUEST_PARTIALLY_APPROVED'
const REQUEST_DECLINED = 'REQUEST_DECLINED'
const EVENT_AND_EXPENSE_DOCUMENTATION = 'EVENT_AND_EXPENSE_DOCUMENTATION'
const DOCUMENTATION_FOLLOW_UP = 'DOCUMENTATION_FOLLOW_UP'
const COMPLIANCE = 'COMPLIANCE'
const NOT_A_QUALIFYING_EVENT = 'NOT_A_QUALIFYING_EVENT'
const EXPENSE_DOCUMENTATION_ESTIMATE_ONLY = 'EXPENSE_DOCUMENTATION_ESTIMATE_ONLY'
const DOCUMENTATION_FOLLOW_UP_WITH_CLOSE_OUT_DATE = 'DOCUMENTATION_FOLLOW_UP_WITH_CLOSE_OUT_DATE'
const REQUEST_DECLINED_OVER_GRANT_LIMIT = 'REQUEST_DECLINED_OVER_GRANT_LIMIT'

/**
 * Given a template name and a request, render the template.
 *
 * Example:
 *
 *    MAP[templateName](request)
 */
export const MAP = {
  COMPLIANCE: {
    category: options.OTHER,
    text: (request, adminUser) => `Hi ${request.employeeRef.firstName},

Thank you for submitting a Grant Circle request.  My name is ${adminUser.firstName} and I will be working with you on your request.

The ${request.organizationRef.name} Grant Circle was established to provide relief during financial hardships. In this case, a financial hardship is when one or more unusual or unexpected events occurs, making it difficult to afford basic living expenses.

You indicated in your application that your financial hardship did not make it difficult for you to afford basic living expenses.

You indicated in your application that your financial hardship was not unexpected or outside of our control.

If that is not accurate, please go back to your request and update your answer in the grant application.

To be compliant with both fund and IRS requirements, we do need to confirm that requests fall within the purpose of the fund. While we do not require proof that savings were exhausted, the fund is not meant to preclude employees from using their own emergency savings when those are available.  So, for compliance purposes, please clarify how the _______ emergency created a financial hardship for you and your household; that is, how did they make it hard for you to afford basic living expenses?

We are sorry to hear about the challenges you are experiencing and hope to hear from you soon.

Thank you,
Grant Circle Team
    `,
    textEs: (request, adminUser) => {
      return `Hola ${request.employeeRef.firstName},

Gracias por entregar una solicitud de Grant Circle (círculo de ayuda). Mi nombre es ${adminUser.firstName} y voy a trabajar al servicio de usted en su solicitud.

El ${request.organizationRef.name} Grant Circle fue fundado para dar ayuda durante las dificultades económicas. En este caso, una dificultad económica es cuando uno o más eventos inesperados o inusuales ocurren.

Usted indicó en su solicitud que su dificultad económica no le dificultó cubrir los gastos de vida básicos.

Usted indicó en su solicitud que su dificultad económica no era inesperada o fuera de su control.

Usted indicó en su solicitud que recibió un cheque de estímulo económico del gobierno federal en 2020.

Si este no está correcto, por favor, regrese a su solicitud y varíe su respuesta en la solicitud.

Para cumplir con los requisitos del fondo y del Servicio de Rentas Internas (Internal Revenue Service - IRS), nosotros necesitamos confirmar que las solicitudes pertenecen al propósito del fondo. Mientras no requerimos prueba qué los ahorros se agotaron, el fondo no tiene la intención de impedir a los empleados usar sus propios ahorros de emergencia cuando estén disponibles. Entonces, por propósitos de cumplimiento, por favor aclare cómo la ____________ emergencia generó una dificultad económica para usted y su hogar: o sea, ¿cómo le dificultó cubrir los gastos de vida básicos?

Lamentamos saber los desafíos que está pasando y esperamos su respuesta pronto.

Saludos,
Equipo Grant Circle
  `
    },
  },

  REQUEST_APPROVED: {
    category: options.GRANT_DECISION,
    text: (request, adminUser) => `Hi ${request.employeeRef.firstName},

We are happy to let you know that we have approved your grant request for ${request.amountDisplay}.

The grant funds will be sent to the PayPal account whose email you provided in your grant request. Funds should reach your PayPal account shortly.

We hope this provides some relief during this challenging time.

Best,
Grant Circle Team
  `,
    textEs: (request, adminUser) => {
      return `Hola ${request.employeeRef.firstName},

Nos alegramos de dejarle saber que hemos aprobado su solicitud de ayuda económica en el monto de ${request.amountDisplay}.

Los fondos de ayuda se enviarán a la cuenta de PayPal asociada con la dirección de correo electrónico usted usó en su solicitud. Los fondos deberían llegar a su cuenta de PayPal pronto.

Nosotros esperamos que esto lo ayude durante este tiempo desafiante.

Saludos,
Equipo Grant Circle
      `
    },
  },

  REQUEST_PARTIALLY_APPROVED: {
    category: options.GRANT_DECISION,
    text: (request, adminUser) => `Hi ${request.employeeRef.firstName},

We are happy to let you know that we have approved a grant of ${request.approvedFundsDisplay}.

The partial approval is due to the per-person grant limit of ${formatNumberAsUSD(
      request.employeeRef.organizationRef.grantLimit,
    )}.

 The partial approval is because ${
      request.approvedFundsDisplay
      } is the most we can approve with the documents included with your grant application.  If you need further help with this or another hardship in future, you may reapply with the required documents.  You are still eligible to receive up to $______ through the Grant Circle.

The grant funds will be sent to the PayPal account whose email you provided in your grant request. Funds should reach your PayPal account shortly.

We hope this provides some relief during this challenging time.

Best,
Grant Circle Team
  `,
    textEs: (request, adminUser) => {
      return `Hola ${request.employeeRef.firstName},

Nos alegramos de dejarle saber que hemos aprobado su solicitud de ayuda económica en el monto de ${
        request.approvedFundsDisplay
        }.

La aprobación parcial se debe al límite de ayuda por persona de ${formatNumberAsUSD(
          request.employeeRef.organizationRef.grantLimit,
        )}.

La aprobación parcial se debe a que ${
        request.approvedFundsDisplay
        }  es lo máximo que podemos aprobar con los documentos incluidos en su solicitud de ayuda. Si necesita más ayuda para esta u otra dificultad en el futuro, puede volver a solicitarla con los documentos obligados. Todavía puede recibir hasta $______ a través del Grant Circle.

Los fondos de ayuda se enviarán a la cuenta de PayPal asociada con la dirección de correo electrónico usted usó en su solicitud. Los fondos deberían llegar a su cuenta de PayPal pronto.

Nosotros esperamos que esto lo ayude durante este tiempo desafiante.

Saludos,
Equipo de Grant Circle
      `
    },
  },

  REQUEST_DECLINED: {
    category: options.GRANT_DECISION,
    text: (request, adminUser) => `Hi ${request.employeeRef.firstName},

Per our previous communication, we are closing out this request today.  We are unable to approve your request at this time, but encourage you to reapply in future if you experience an eligible emergency and need additional help.

For more information on Grant Circle eligibility and application requirements, please see the Frequently Asked Questions (${request.organizationRef.url}/faq).

Let us know if you have any questions.

Best,
Grant Circle Team
  `,
    textEs: (request, adminUser) => {
      return `Hola ${request.employeeRef.firstName},

De acuerdo con nuestra comunicación anterior, estamos cerrando esta solicitud hoy. No podemos aprobar su solicitud en este momento, pero, le animamos a que vuelva a solicitarla en el futuro si tiene una emergencia elegible y necesita ayuda adicional.

Para obtener más información sobre la elegibilidad de Grant Circle y los requisitos de solicitud, consulte las preguntas frecuentes (${request.organizationRef.url}/faq).

Avísenos si tiene alguna pregunta.

Saludos,
Equipo de Grant Circle
`
    },
  },
  EVENT_AND_EXPENSE_DOCUMENTATION: {
    category: options.DOCUMENTATION,
    text: (request, adminUser) => `Hi ${request.employeeRef.firstName},

Thank you for submitting a Grant Circle request. My name is ${adminUser.firstName} and I will be working with you on your request.

We need more information from you to process your request.

To add documents to your request log into the Grant Circle go to My Activity, click on your grant request, and click "edit" on the Request Summary page.

If you have questions, you can reply to this message or consult the Frequently Asked Questions: ${request.organizationRef.url}/faq.

We are sorry to hear about the challenges you are experiencing and hope to hear from you soon.

Thank you,
Grant Circle Team
  `,
    textEs: (request, adminUser) => {
      return `Hola ${request.employeeRef.firstName},

Gracias por entregar una solicitud de Grant Circle. Mi nombre es ${adminUser.firstName} y voy a trabajar al servicio de usted en su solicitud.

Necesitamos más información para seguir con su solicitud.

Para adjuntar los documentos en la solicitud: inicie sesión en Grant Circle, vaya a Mi Actividad, haga clic en su solicitud y haga clic en “editar” en la página Solicitud Resumen.

Si usted tiene preguntas, puede responder a este mensaje o consultar las preguntas frecuentes: ${request.organizationRef.url}/faq.

Lamentamos saber los desafíos que está pasando y esperamos su respuesta pronto.

Saludos,
Equipo Grant Circle
      `
    },
  },
  DOCUMENTATION_FOLLOW_UP: {
    category: options.DOCUMENTATION,
    text: (request, adminUser) => `Hi ${request.employeeRef.firstName},

I am following up to make sure you received our previous message.  We are still missing information that we need to process your Grant Circle request.

You can also find more detail on request requirements in the Frequently Asked Questions: ${request.organizationRef.url}/faq.

Best,
Grant Circle Team
  `,
    textEs: (request, adminUser) => {
      return `Hola ${request.employeeRef.firstName},

Le escribo a usted de nuevo para verificar que recibió nuestro mensaje previo. Aún falta información que necesitamos para seguir con su solicitud de Grant Circle.

Puede encontrar más detalles sobre requisitos de solicitudes en las preguntas frecuentes: ${request.organizationRef.url}/faq.

Saludos,
Equipo Grant Circle
      `
    },
  },
  EXPENSE_DOCUMENTATION_ESTIMATE_ONLY: {
    category: options.DOCUMENTATION,
    text: (request, adminUser) => `Hi ${request.employeeRef.firstName},

Thank you for submitting a Grant Circle request.  My name is ${adminUser.firstName} and I will be working with you on your request.

In order to process your grant request, we need more information. While your _______ serves as documentation that _________ happened, we cannot accept it as your expense documentation because it is an estimate and, therefore, not an expense you have definitely incurred.

In order to fund your grant, we need documentation showing expenses that you are responsible for (i.e. have a bill due in 30 days) or that you have already paid for (i.e. have a receipt) that you'll have trouble affording because of the expenses detailed in the estimate. These might include rent, utilities, or other living expenses.

Please upload expense documentation on the “Document Your Expenses” page of the application. Documents must include your name, the date, the biller name, and amount owed or paid. (Note: You can request up to $200 for food without additional expense documentation.)

We are sorry to hear about the challenges you are experiencing and hope to hear from you soon.

Thank you,
Grant Circle Team
  `,
    textEs: (request, adminUser) => {
      return `Hola ${request.employeeRef.firstName},

Gracias por entregar una solicitud de Grant Circle. Mi nombre es ${adminUser.firstName} y voy a trabajar al servicio de usted en su solicitud.

Necesitamos más información para seguir con su solicitud. A pesar de que su ________ funciona como documentación que ______ pasó, no podemos aceptarlo como documentación de gastos porque su documento es una estimación de costos y así no es un gasto que usted ya ha pagado.

Para financiar su ayuda económica, necesitamos documentación que demuestre los gastos de que usted es responsable (esto es, tener una factura que vence en 30 días).

En lugar de una factura que vence que en el futuro necesitaríamos documentación de gastos habituales pagados (esto es, tener un recibo) que se complicarán permitirle los gastos de la estimación de costos. Podrían incluir el alquiler, agua y luz o otros gastos de vida básicos.

Por favor, suba la documentación de gastos en la página “Documente sus gastos” de la solicitud. Documentos deben incluir su nombre y apellido, la fecha, el nombre del cobrador, y el monto adecuado o pagado. (Nota: Usted puede solicitar hasta $200 por los alimentos sin documentación de gastos.)

Lamentamos saber los desafíos que está pasando y esperamos su respuesta pronto.

Saludos,
Equipo Grant Circle
      `
    },
  },
  DOCUMENTATION_FOLLOW_UP_WITH_CLOSE_OUT_DATE: {
    category: options.DOCUMENTATION,
    text: (request, adminUser) => `Hi ${request.employeeRef.firstName},

I am following up to make sure you received our previous messages.  We are still missing information for your Grant Circle request and will not be able to move forward if we do not hear from you.

Please let us know if you have questions about what is missing or incomplete.

If we do not hear from you, we will close out this request on __________.

Best,
Grant Circle Team
  `,
    textEs: (request, adminUser) => {
      return `Hola ${request.employeeRef.firstName},

Le escribo a usted de nuevo para verificar que recibió nuestros mensajes previos. Aún falta información para su solicitud de Grant Circle y no podremos continuar si no recibimos ningún mensaje de usted.

Por favor, avísenos si usted tiene alguna pregunta sobre qué falta o está aún incompleto.

Si no recibiremos ninguna respuesta de usted, cerraremos esta solicitud el __________.

Saludos,
Equipo de Grant Circle`
    },
  },
  REQUEST_DECLINED_OVER_GRANT_LIMIT: {
    category: options.GRANT_DECISION,
    text: (request, adminUser) => `Hi ${request.employeeRef.firstName},

Unfortunately, we are unable to approve your request as you have already received the maximum grant of $_________.

For more information on Grant Circle limits, please see the FAQ: ${request.organizationRef.url}/faq/#10.

[----Fill in other resources, if applicable.----]

We are sorry to hear about the challenges you are experiencing and wish you the best.

Thank you,
Grant Circle Team
  `,
    textEs: (request, adminUser) => {
      return `Hola ${request.employeeRef.firstName},

Lamentablemente, no podemos aprobar su solicitud porque ya recibió la ayuda máxima de $_________.

Para más información sobre los límites del Grant Circle, consulte las preguntas frecuentes: ${request.organizationRef.url}/faq/#10.

[----Fill in other resources, if applicable.----]

Lamentamos saber los desafíos que está pasando y le deseamos lo mejor.

Saludos,
Equipo de Grant Circle
      `
    },
  },
  NOT_A_QUALIFYING_EVENT: {
    category: options.DOCUMENTATION,
    text: (request, adminUser) => `Hi ${request.employeeRef.firstName},

Thank you for submitting a Grant Circle request.  My name is ${adminUser.firstName} and I will be working with you on your request.

Unfortunately, ______ is not a qualifying hardship for this program, which means it is not eligible for grant support.

If you have experienced an emergency in the last 12 months that is a qualifying hardship, you may update your application.

Without an eligible, documented hardship we will be unable to fund a grant. More detail about qualifying hardships and required documentation is available in the FAQ: ${request.organizationRef.url}/FAQ/#4.

We are sorry to hear about the challenges you are experiencing and wish you the best.

Thank you,
Grant Circle Team
    `,
    textEs: (request, adminUser) => {
      return `Hola ${request.employeeRef.firstName},

Gracias por entregar una solicitud de Grant Circle. Mi nombre es ${adminUser.firstName} y voy a trabajar al servicio de usted en su solicitud.

Desafortunadamente, _______ no es una situación de dificultad que aplica para este programa, así su solicitud no está calificada para ayuda económica.

Si usted ha pasado una emergencia en los últimos 12 meses que aplica a las dificultades económicas de nuestro programa, usted puede actualizar su solicitud.

Sin una dificultad económica que aplica a nuestro programa y que esté documentada, no podemos financiar una ayuda económica. Más detalles sobre las dificultades que aplican y la documentación requisita están disponibles en las preguntas frecuentes: ${request.organizationRef.url}/FAQ/#4

Planeamos cerrar esta solicitud el _________, pero si tiene cualquier pregunta, por favor dejarnos saber.
Lamentamos saber los desafíos que está pasando y le deseamos lo mejor.

Gracias,
Equipo Grant Circle
      `
    },
  },
}

export const CHOICES = [
  {
    value: COMPLIANCE,
    label: 'Compliance',
  },
  {
    value: DOCUMENTATION_FOLLOW_UP_WITH_CLOSE_OUT_DATE,
    label: 'Documentation Follow-Up, With Close Out Date',
  },
  {
    value: DOCUMENTATION_FOLLOW_UP,
    label: 'Documentation Second Follow-up',
  },
  {
    value: EVENT_AND_EXPENSE_DOCUMENTATION,
    label: 'Event and Expense Documentation',
  },
  {
    value: EXPENSE_DOCUMENTATION_ESTIMATE_ONLY,
    label: 'Expense Documentation, Estimate Only',
  },
  {
    value: NOT_A_QUALIFYING_EVENT,
    label: 'Not A Qualifying Event',
  },
  {value: REQUEST_APPROVED, label: 'Request Approved'},
  {value: REQUEST_DECLINED, label: 'Request Declined'},
  {
    value: REQUEST_DECLINED_OVER_GRANT_LIMIT,
    label: 'Request Declined, Over Grant Limit',
  },
  {
    value: REQUEST_PARTIALLY_APPROVED,
    label: 'Request Partially Approved',
  },
]

export default {
  REQUEST_APPROVED,
  REQUEST_PARTIALLY_APPROVED,
  REQUEST_DECLINED,
  DOCUMENTATION_FOLLOW_UP,
  EVENT_AND_EXPENSE_DOCUMENTATION,
  COMPLIANCE,
  NOT_A_QUALIFYING_EVENT,
  EXPENSE_DOCUMENTATION_ESTIMATE_ONLY,
  DOCUMENTATION_FOLLOW_UP_WITH_CLOSE_OUT_DATE,
  REQUEST_DECLINED_OVER_GRANT_LIMIT,
  MAP,
  CHOICES,
}
